import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
const STATUS_COLORS = { not_started: '#62615D', in_progress: '#3B82F6', review: '#F59E0B', filed: '#10B981', on_hold: '#EF4444' }

export default function CalendarPage() {
  const { profile } = useAuth()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [today] = useState(new Date())
  const [viewDate, setViewDate] = useState(new Date())
  const [hoveredDay, setHoveredDay] = useState(null)

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('clients')
        .select('id, full_name, business_name, entity_type, status, deadline')
        .eq('cpa_id', profile.id)
        .not('deadline', 'is', null)
        .order('deadline', { ascending: true })
      setClients(data || [])
      setLoading(false)
    }
    load()
  }, [])

  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  function getClientsForDay(day) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return clients.filter(c => c.deadline?.startsWith(dateStr))
  }

  function prevMonth() {
    setViewDate(new Date(year, month - 1, 1))
  }

  function nextMonth() {
    setViewDate(new Date(year, month + 1, 1))
  }

  // Get upcoming deadlines
  const upcoming = clients
    .filter(c => c.deadline && new Date(c.deadline) >= today)
    .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
    .slice(0, 10)

  const overdue = clients.filter(c => c.deadline && new Date(c.deadline) < today && c.status !== 'filed')

  if (loading) return (
    <div style={{ display: 'grid', gap: 10 }}>
      {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80 }} />)}
    </div>
  )

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20 }}>
      {/* Calendar */}
      <div>
        {/* Header */}
        <div className="card" style={{ padding: '20px 24px', marginBottom: 16, background: '#202326', border: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button onClick={prevMonth} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#CBB17B', fontSize: 18, cursor: 'pointer', borderRadius: 8, width: 36, height: 36 }}>‹</button>
            <h2 style={{ color: '#fff', fontSize: 20, margin: 0 }}>{MONTHS[month]} {year}</h2>
            <button onClick={nextMonth} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#CBB17B', fontSize: 18, cursor: 'pointer', borderRadius: 8, width: 36, height: 36 }}>›</button>
          </div>
        </div>

        {/* Calendar grid */}
        <div className="card" style={{ padding: '16px', overflow: 'hidden' }}>
          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 8 }}>
            {DAYS.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#62615D', padding: '4px 0', textTransform: 'uppercase' }}>{d}</div>
            ))}
          </div>

          {/* Calendar days */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {/* Empty cells before first day */}
            {[...Array(firstDay)].map((_, i) => <div key={`empty-${i}`} />)}

            {/* Day cells */}
            {[...Array(daysInMonth)].map((_, i) => {
              const day = i + 1
              const dayClients = getClientsForDay(day)
              const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year
              const isPast = new Date(year, month, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate())

              return (
                <div key={day}
                  onMouseEnter={() => dayClients.length > 0 && setHoveredDay(day)}
                  onMouseLeave={() => setHoveredDay(null)}
                  style={{
                    minHeight: 72, padding: '6px 8px', borderRadius: 8,
                    background: isToday ? '#F1E8D7' : dayClients.length > 0 ? '#F0F9FF' : '#FAFAF8',
                    border: `1px solid ${isToday ? '#CBB17B' : dayClients.length > 0 ? '#BAE6FD' : 'transparent'}`,
                    position: 'relative', cursor: dayClients.length > 0 ? 'pointer' : 'default'
                  }}>
                  <div style={{
                    fontSize: 12, fontWeight: isToday ? 700 : 400,
                    color: isToday ? '#765B2C' : isPast ? '#9CA3AF' : '#202326',
                    marginBottom: 4
                  }}>{day}</div>

                  {/* Client deadline dots */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {dayClients.slice(0, 3).map(c => (
                      <div key={c.id} style={{
                        fontSize: 10, fontWeight: 600, padding: '2px 5px', borderRadius: 4,
                        background: STATUS_COLORS[c.status] + '20',
                        color: STATUS_COLORS[c.status],
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {c.business_name || c.full_name}
                      </div>
                    ))}
                    {dayClients.length > 3 && (
                      <div style={{ fontSize: 10, color: '#62615D' }}>+{dayClients.length - 3} more</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
          {Object.entries(STATUS_COLORS).map(([status, color]) => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
              <span style={{ color: '#62615D', textTransform: 'capitalize' }}>{status.replace('_', ' ')}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Sidebar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Overdue */}
        {overdue.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: 'hidden', border: '1px solid #FCA5A5' }}>
            <div style={{ background: '#FEE2E2', padding: '12px 16px', borderBottom: '1px solid #FCA5A5' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#991B1B' }}>🚨 Overdue ({overdue.length})</div>
            </div>
            {overdue.map((c, i) => (
              <div key={c.id} style={{ padding: '10px 16px', borderBottom: i < overdue.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#202326' }}>{c.business_name || c.full_name}</div>
                <div style={{ fontSize: 11, color: '#991B1B' }}>Due: {new Date(c.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
              </div>
            ))}
          </div>
        )}

        {/* Upcoming */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ background: '#F7F3EB', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#202326' }}>📅 Upcoming Deadlines</div>
          </div>
          {upcoming.length === 0 ? (
            <div style={{ padding: '20px 16px', textAlign: 'center', fontSize: 13, color: '#62615D' }}>No upcoming deadlines</div>
          ) : (
            upcoming.map((c, i) => {
              const days = Math.ceil((new Date(c.deadline) - today) / (1000 * 60 * 60 * 24))
              return (
                <div key={c.id} style={{ padding: '10px 16px', borderBottom: i < upcoming.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#202326' }}>{c.business_name || c.full_name}</div>
                      <div style={{ fontSize: 11, color: '#62615D' }}>{new Date(c.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                      background: days <= 7 ? '#FEE2E2' : days <= 14 ? '#FEF3C7' : '#D1FAE5',
                      color: days <= 7 ? '#991B1B' : days <= 14 ? '#92400E' : '#065F46'
                    }}>
                      {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days}d`}
                    </span>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
